import type { ConditionDTO, TierDTO } from '@uturn/api/v1';
import { ConditionDTOType, TierDTOType } from '@uturn/api/v1';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PricingTierCard } from './pricing-tier-cards';
import type { PricingTiersProps } from './pricing-tiers.types';

function getTierButtonLabel(
	t: TFunction<'translation', undefined>,
	onCurrentTier: boolean,
	onLowerTier: boolean,
): string {
	if (onCurrentTier) {
		return t(
			'pages.subscriptions.submit.label.current',
			'Current subscription',
		);
	}
	if (onLowerTier) {
		return t('pages.subscriptions.submit.label.contact', 'Contact UTURN');
	}
	return t('pages.subscriptions.submit.label.default', 'Subscribe');
}

function getFeatures(t: TFunction<'translation', undefined>, tier: TierDTO) {
	const conditions = tier.conditions;

	if (!conditions?.length) {
		return [];
	}

	const applicableConditions = conditions.filter(
		(condition) =>
			!(
				condition.type === ConditionDTOType.BOOLEAN &&
				condition.value === 'false'
			),
	);

	if (!applicableConditions.length) {
		return [];
	}

	return applicableConditions.map((condition) => {
		const tierConditionKey = condition.key.toLowerCase();

		if (condition.type === ConditionDTOType.BOOLEAN) {
			return t(
				`pages.subscriptions.tier.condition.${tierConditionKey}`,
				condition.key,
			);
		}

		// TECH-671: Adjust "Custom" tier to show "Custom requests per month" instead of
		//           "1000 requests per month".
		// TODO: Ultimately, this should be fixed in the backend. See ticket reference above.
		// NOTE: Don't use strict equals here because of non-ascii characters found in the data.
		if (!condition.value || tier.name.startsWith('Custom')) {
			return t(
				`pages.subscriptions.tier.condition.${tierConditionKey}_valueless`,
				condition.key,
			);
		}

		return t(
			`pages.subscriptions.tier.condition.${tierConditionKey}`,
			`${condition.key} {{ conditionValue }}`,
			{ conditionValue: condition.value },
		);
	});
}

export function PricingTiers({
	subscription,
	tiers,
	onSelect,
}: PricingTiersProps) {
	const { t } = useTranslation();

	if (!tiers?.length) {
		return null;
	}

	const indexCurrentTier =
		tiers.findIndex(
			(item) => subscription && item.name === subscription.tierName,
		) ?? -1;

	const currentTierId =
		indexCurrentTier === -1 ? -1 : (tiers[indexCurrentTier].id ?? -1);

	const lowerTierIds =
		indexCurrentTier === -1
			? []
			: tiers
					.filter((item, index) => !!item.id && index < indexCurrentTier)
					.map((item) => item.id as number); // Guarantied by Type Predicate Inference

	return (
		<div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
			{tiers
				.filter((tier) => !!tier.id && tier.type !== TierDTOType.TRIAL)
				.map((tier) => {
					const tierId = tier.id as number; // Guarantied by Type Predicate Inference
					const tierNameKey = tier.name.toLowerCase();

					const onCurrentTier = tierId === currentTierId;
					const onLowerTier = lowerTierIds.includes(tierId);

					return (
						<PricingTierCard
							key={tierId.toString()}
							id={tierId.toString()}
							mostPopular={true}
							name={t(
								`pages.subscriptions.tier.name.${tierNameKey}`,
								tier.name,
							)}
							features={getFeatures(t, tier)}
							price={
								tier.price ? `€ ${tier.price}` : t('general.custom', 'Custom')
							}
							billingFrequency={tier.price ? tier.paymentPeriod : undefined}
							description={''}
							action={{
								label: getTierButtonLabel(t, onCurrentTier, onLowerTier),
								handleOnClick:
									onCurrentTier || onLowerTier
										? undefined
										: () => onSelect(tier),
							}}
						/>
					);
				})}
		</div>
	);
}
